<template>
    <v-row class="ma-0 pa-0" align="center" justify="center">
      <v-col cols="12" class="chart_style">
        <div class="chart_legend_bar">
          <v-menu offset-y content-class="audience_filter_menu">
          <template v-slot:activator="{ on, attrs }">
            <v-btn
              class="audience_filter_button"
              v-bind="attrs"
              v-on="on"
              depressed
              small
            >
              <v-icon left small>mdi-account-filter-outline</v-icon>
              <span class="audience_filter_button__label">
                {{ selected_audience || "All audiences" }}
              </span>
              <v-icon right small>mdi-chevron-down</v-icon>
            </v-btn>
          </template>
          <v-list dense class="audience_filter_list">
            <v-list-item
              :class="{ 'audience_filter_list__item--active': !selected_audience }"
              @click="select_audience(null)"
            >
              <v-list-item-content>
                <v-list-item-title>All audiences</v-list-item-title>
              </v-list-item-content>
              <v-list-item-icon v-if="!selected_audience">
                <v-icon small>mdi-check</v-icon>
              </v-list-item-icon>
            </v-list-item>
            <v-divider></v-divider>
            <v-list-item
              v-for="audience in audiences"
              :key="audience"
              :class="{ 'audience_filter_list__item--active': selected_audience === audience }"
              @click="select_audience(audience)"
            >
              <v-list-item-content>
                <v-list-item-title>{{ audience }}</v-list-item-title>
              </v-list-item-content>
              <v-list-item-icon v-if="selected_audience === audience">
                <v-icon small>mdi-check</v-icon>
              </v-list-item-icon>
            </v-list-item>
          </v-list>
          </v-menu>
          <v-menu offset-y content-class="audience_filter_menu">
            <template v-slot:activator="{ on, attrs }">
              <v-btn
                class="audience_filter_button"
                v-bind="attrs"
                v-on="on"
                depressed
                small
              >
                <v-icon left small>mdi-shape-outline</v-icon>
                <span class="audience_filter_button__label">
                  {{ selected_format || "All formats" }}
                </span>
                <v-icon right small>mdi-chevron-down</v-icon>
              </v-btn>
            </template>
            <v-list dense class="audience_filter_list">
              <v-list-item
                :class="{ 'audience_filter_list__item--active': !selected_format }"
                @click="select_format(null)"
              >
                <v-list-item-content>
                  <v-list-item-title>All formats</v-list-item-title>
                </v-list-item-content>
                <v-list-item-icon v-if="!selected_format">
                  <v-icon small>mdi-check</v-icon>
                </v-list-item-icon>
              </v-list-item>
              <v-divider></v-divider>
              <v-list-item
                v-for="format in formats"
                :key="format"
                :class="{ 'audience_filter_list__item--active': selected_format === format }"
                @click="select_format(format)"
              >
                <v-list-item-content>
                  <v-list-item-title>{{ format }}</v-list-item-title>
                </v-list-item-content>
                <v-list-item-icon v-if="selected_format === format">
                  <v-icon small>mdi-check</v-icon>
                </v-list-item-icon>
              </v-list-item>
            </v-list>
          </v-menu>
          <div class="series_legend" aria-label="Chart series">
            <button
              v-for="channel in visible_channels"
              :key="channel"
              class="series_legend__item"
              :class="{ 'series_legend__item--hidden': is_channel_hidden(channel) }"
              type="button"
              :aria-pressed="!is_channel_hidden(channel)"
              :title="channel"
              @click="toggle_channel(channel)"
            >
              <span
                class="series_legend__swatch"
                :style="{ backgroundColor: get_channel_color(channel) }"
              ></span>
              <span class="series_legend__label">{{ channel }}</span>
            </button>
          </div>
        </div>
        <v-chart
          :id="chart_id"
          :ref="chart_ref"
          class="chart"
          :key="chart_render_key"
          :option="option"
          autoresize
        ></v-chart>
      </v-col>
    </v-row>
  </template>
  
  <script>
  import VChart, { THEME_KEY } from "vue-echarts";
  import "echarts";
  import { use } from "echarts/core";
  import { CanvasRenderer } from "echarts/renderers";
  import { TitleComponent, TooltipComponent, LegendComponent } from "echarts/components";
  
  use([CanvasRenderer, TitleComponent, TooltipComponent, LegendComponent]);

  const METRIC_OPTIONS = {
    reach: {
      pacing: {
        column: "Reach Pacing",
        axis_label: "Unique Reach %",
        value_type: "percent",
      },
      cumulative: {
        column: "Reach",
        axis_label: "Unique Reach %",
        value_type: "percent",
      },
    },
    impressions: {
      pacing: {
        column: "Impressions Pacing",
        axis_label: "Impressions",
        value_type: "number",
      },
      cumulative: {
        column: "Impressions",
        axis_label: "Impressions",
        value_type: "number",
      },
    },
    frequency: {
      pacing: {
        column: "Frequency Pacing",
        axis_label: "Avg. Frequency",
        value_type: "decimal",
      },
      cumulative: {
        column: "Frequency",
        axis_label: "Avg. Frequency",
        value_type: "decimal",
      },
    },
  };

  export default {
    name: "ReachChartV2",
    props: [
      "chart_title",
      "chart_data",
      "chart_id",
      "chart_ref",
      "chart_options",
      "raw_data_type",
      "raw_data_stat",
      "audience_options",
      "format_options",
    ],
    components: {
      VChart,
    },
    provide: {
      [THEME_KEY]: "light",
    },
    data() {
      return {
        selected_audience: null,
        selected_format: null,
        hidden_channels: [],
      };
    },
    computed: {
      chart_render_key: function() {
        return [
          this.chart_id,
          this.raw_data_type,
          this.raw_data_stat,
          this.selected_audience || "all-audiences",
          this.selected_format || "all-formats",
          this.hidden_channels.join("|"),
          this.chart_data ? this.chart_data.length : 0,
        ].join("-");
      },
      channels: function() {
        const chartData = Array.isArray(this.chart_data) ? this.chart_data : [];
        const channels = [];

        chartData.forEach((row) => {
          if (row[0] !== "Face ID" && !channels.includes(row[0])) {
            channels.push(row[0]);
          }
        });

        return channels;
      },
      audiences: function() {
        const providedAudiences = Array.isArray(this.audience_options)
          ? this.audience_options
              .filter((audience) => audience != null && String(audience).trim() !== "")
              .map((audience) => String(audience).trim())
          : [];

        if (providedAudiences.length) {
          return [...new Set(providedAudiences)];
        }

        const audienceOnlyChannels = this.channels.filter((candidate) => {
          return this.channels.some((channel) => {
            return channel !== candidate && this.channel_belongs_to_audience(channel, candidate);
          });
        });

        if (audienceOnlyChannels.length) {
          return audienceOnlyChannels;
        }

        return [...new Set(this.channels.map((channel) => {
          return this.split_channel_name(channel).audience;
        }))];
      },
      formats: function() {
        const providedFormats = Array.isArray(this.format_options)
          ? this.format_options
              .filter((format) => format != null && String(format).trim() !== "")
              .map((format) => String(format).trim())
          : [];

        if (providedFormats.length) {
          return [...new Set(providedFormats)];
        }

        return [...new Set(this.channels
          .map((channel) => this.split_channel_name(channel).format)
          .filter((format) => format))];
      },
      visible_channels: function() {
        return this.channels.filter((channel) => {
          const audienceMatches = !this.selected_audience
            || this.get_channel_audience(channel) === this.selected_audience;
          const formatMatches = !this.selected_format
            || this.get_channel_format(channel) === this.selected_format;
          return audienceMatches && formatMatches;
        });
      },
      displayed_channels: function() {
        return this.visible_channels.filter((channel) => {
          return !this.hidden_channels.includes(channel);
        });
      },
      selected_metric: function() {
        const stat = this.raw_data_stat || "reach";
        const type = this.raw_data_type || "cumulative";
        const metricOptions = METRIC_OPTIONS[stat] || METRIC_OPTIONS.reach;

        return metricOptions[type] || metricOptions.cumulative;
      },
      chart_type: function() {
        return this.raw_data_type === "pacing" ? "bar" : "line";
      },
      chart_title_text: function() {
        const metricLabelMap = {
          reach: "Reach",
          impressions: "Impressions",
          frequency: "Frequency",
        };
        const dataTypeLabel = this.raw_data_type === "cumulative" ? "Cumulative" : "Pacing";
        const metricLabel = metricLabelMap[this.raw_data_stat] || this.selected_metric.column;
        return dataTypeLabel + " " + metricLabel + " Analysis";
      },
      option: function() {
        const chartData = Array.isArray(this.chart_data) ? this.chart_data : [];
        const headers = chartData[0] || [];
        const selectedMetric = this.resolve_metric(headers);
        const selectedMetricIndex = headers.indexOf(selectedMetric.column);
        const channels = this.displayed_channels;

        const datasetWithFilters = [];
        const seriesList = [];
        channels.forEach((channel) => {
          var datasetId = 'dataset_' + channel;
          const channelColor = this.get_channel_color(channel);
          datasetWithFilters.push({
            id: datasetId,
            fromDatasetId: 'dataset_raw',
            transform: {
                type: 'filter',
                config: {
                  and: [
                      { dimension: 'Face ID', '=': channel }
                  ]
                }
            }
          });
          let seriesConfig = {
            type: this.chart_type,
            datasetId: datasetId,
            name: channel,
            endLabel: {
                show: false,
                formatter: function (params) {
                return params.value[3] + ': ' + params.value[0];
                }
            },
            labelLayout: {
                moveOverlap: 'shiftY'
            },
            emphasis: {
                focus: 'series'
            },
            itemStyle: {
                color: channelColor
            },
            lineStyle: {
                color: channelColor
            },
            encode: {
                x: 'Date',
                y: selectedMetric.column,
                label: ['Face ID', selectedMetric.column],
                itemName: 'Date',
                tooltip: [selectedMetric.column]
            }
          };

          if (this.chart_type === "line") {
            seriesConfig.showSymbol = false;
          } else {
            seriesConfig.barCategoryGap = "5%";
            seriesConfig.barGap = "0%";
          }

          seriesList.push(seriesConfig);
        });

        return {
          animationDuration: 1000,
          dataset: [
              {
                  id: 'dataset_raw',
                  source: chartData, 
              },
              ...datasetWithFilters
          ],
          color: [
            "#70E000", "#A4133C", "#2AB7CA", "#3A0CA3", "#F6BD60", "#1F4E5F", "#F28482",
            "#2F5D50", "#9FE0D0", "#A44A3F", "#CDB4DB", "#D7263D", "#75B9E3", "#6B8E23",
            "#E8B4B8", "#1E3A8A", "#F77F00", "#6D597A", "#A7C957", "#D0006F", "#B7D3E9",
            "#4A6F46", "#F4A261", "#2E5AAC", "#9CAF88", "#7B2CBF", "#C19A6B", "#00B4D8",
            "#C08497", "#2A9D8F", "#E9C46A", "#3A86C8", "#B46A55", "#4EC5C1", "#D4A017",
            "#A2678A", "#D8C3A5", "#C06542", "#B8A1D9", "#D6E6F2"
          ],
          title: {
            text: this.chart_title_text,
            fontFamily: "Poppins",
            left: 'center',
          },
          legend: {
            show: false,
          },
          tooltip: {
              order: 'valueAsc',
              trigger: 'axis',
              formatter: (params) => {
                if (!params || !params.length) {
                  return "";
                }
                const title = params[0].axisValueLabel || params[0].axisValue;

                const body = params.map(item => {
                    const value = this.get_data_value(item.value, selectedMetric.column, selectedMetricIndex);
                    return `${item.marker}${item.seriesName || ''}: ${this.format_metric_value(value, selectedMetric)}`;
                }).join('<br/>');

                return `<strong>${title}</strong><br/>${body}`;
              }
          },
          xAxis: {
              type: 'category',
              axisLabel: {
                  fontSize: 12,
                  fontFamily: "Poppins"
              },
              name: 'Date',
              nameLocation: 'middle',
              nameGap: 30,
              nameTextStyle: {
                  fontSize: 14,
                  fontFamily: "Poppins",
                  fontWeight: 'bold'
              }
          },
          yAxis: {
              axisLabel: {
                  formatter: (value) => {
                      return this.format_axis_value(value, selectedMetric);
                  },
                  fontSize: 12,
                  fontFamily: "Poppins"
              },
              name: selectedMetric.axis_label,
              nameLocation: 'middle',
              nameGap: 40,
              nameTextStyle: {
                  fontSize: 14,
                  fontFamily: "Poppins",
                  fontWeight: 'bold'
              }
          },
          grid: {
              left: '4%',
              right: '3%',
              top: '16%',
              bottom: '7%',
              containLabel: true
          },
          series: seriesList,
        };
      }
    },
    methods: {
      select_audience(audience) {
        this.selected_audience = audience;
        this.hidden_channels = [];
      },
      select_format(format) {
        this.selected_format = format;
        this.hidden_channels = [];
      },
      toggle_channel(channel) {
        if (this.hidden_channels.includes(channel)) {
          this.hidden_channels = this.hidden_channels.filter((item) => item !== channel);
        } else if (this.displayed_channels.length > 1) {
          this.hidden_channels = [...this.hidden_channels, channel];
        }
      },
      is_channel_hidden(channel) {
        return this.hidden_channels.includes(channel);
      },
      get_channel_color(channel) {
        const colorIndex = Math.max(this.channels.indexOf(channel), 0);
        const colors = [
          "#70E000", "#A4133C", "#2AB7CA", "#3A0CA3", "#F6BD60", "#1F4E5F", "#F28482",
          "#2F5D50", "#9FE0D0", "#A44A3F", "#CDB4DB", "#D7263D", "#75B9E3", "#6B8E23",
          "#E8B4B8", "#1E3A8A", "#F77F00", "#6D597A", "#A7C957", "#D0006F", "#B7D3E9",
          "#4A6F46", "#F4A261", "#2E5AAC", "#9CAF88", "#7B2CBF", "#C19A6B", "#00B4D8",
          "#C08497", "#2A9D8F", "#E9C46A", "#3A86C8", "#B46A55", "#4EC5C1", "#D4A017",
          "#A2678A", "#D8C3A5", "#C06542", "#B8A1D9", "#D6E6F2"
        ];
        return colors[colorIndex % colors.length];
      },
      split_channel_name(channelName) {
        const cleanChannelName = String(channelName || "");
        const separatorMatches = [...cleanChannelName.matchAll(/\s+-\s+/g)];
        const separatorMatch = separatorMatches[separatorMatches.length - 1];

        if (!separatorMatch) {
          return {
            audience: cleanChannelName.trim(),
            format: null,
          };
        }

        const separatorIndex = separatorMatch.index;
        return {
          audience: cleanChannelName.slice(0, separatorIndex).trim(),
          format: cleanChannelName
            .slice(separatorIndex + separatorMatch[0].length)
            .trim(),
        };
      },
      channel_belongs_to_audience(channelName, audienceName) {
        const channel = String(channelName || "").trim();
        const audience = String(audienceName || "").trim();
        const normalizedChannel = channel.toLocaleLowerCase();
        const normalizedAudience = audience.toLocaleLowerCase();

        if (normalizedChannel === normalizedAudience) {
          return true;
        }

        return normalizedChannel.startsWith(normalizedAudience)
          && /^\s*-\s*/.test(channel.slice(audience.length));
      },
      get_channel_audience(channelName) {
        const matchingAudience = this.audiences
          .slice()
          .sort((first, second) => second.length - first.length)
          .find((audience) => this.channel_belongs_to_audience(channelName, audience));

        return matchingAudience || null;
      },
      get_channel_format(channelName) {
        const channel = String(channelName || "").trim();
        const normalizedChannel = channel.toLocaleLowerCase();
        const matchingFormat = this.formats
          .slice()
          .sort((first, second) => second.length - first.length)
          .find((format) => {
            const normalizedFormat = String(format).trim().toLocaleLowerCase();
            if (!normalizedChannel.endsWith(normalizedFormat)) {
              return false;
            }
            const prefix = channel.slice(0, channel.length - String(format).trim().length);
            return /-\s*$/.test(prefix);
          });

        return matchingFormat || null;
      },
      resolve_metric(headers) {
        if (this.has_metric_column(headers, this.selected_metric.column)) {
          return this.selected_metric;
        }

        const fallbackColumns = [
          "Reach",
          "Reach Pacing",
          "Impressions",
          "Impressions Pacing",
          "Frequency",
          "Frequency Pacing",
        ];
        const fallbackColumn = fallbackColumns.find((metricColumn) => {
          return this.has_metric_column(headers, metricColumn);
        });

        return this.find_metric_by_column(fallbackColumn) || METRIC_OPTIONS.reach.cumulative;
      },
      find_metric_by_column(column) {
        for (let stat of Object.keys(METRIC_OPTIONS)) {
          for (let type of Object.keys(METRIC_OPTIONS[stat])) {
            if (METRIC_OPTIONS[stat][type].column === column) {
              return METRIC_OPTIONS[stat][type];
            }
          }
        }
        return null;
      },
      has_metric_column(headers, metricColumn) {
        return Array.isArray(headers) && headers.includes(metricColumn);
      },
      get_data_value(dataValue, metricColumn, metricIndex) {
        if (Array.isArray(dataValue)) {
          return dataValue[metricIndex];
        }
        if (dataValue && Object.prototype.hasOwnProperty.call(dataValue, metricColumn)) {
          return dataValue[metricColumn];
        }
        return dataValue;
      },
      format_axis_value(value, metric) {
        const numericValue = Number(value);
        if (Number.isNaN(numericValue)) {
          return "";
        }
        if (metric.value_type === "percent") {
          return (numericValue * 100).toFixed(0) + '%';
        }
        if (metric.value_type === "decimal") {
          return numericValue.toFixed(1);
        }
        return Intl.NumberFormat('en', { notation: 'compact', maximumFractionDigits: 1 }).format(numericValue);
      },
      format_metric_value(value, metric) {
        const numericValue = Number(value);
        if (value == null || value === "" || Number.isNaN(numericValue)) {
          return "N/A";
        }
        if (metric.value_type === "percent") {
          return (numericValue * 100).toFixed(1) + '%';
        }
        if (metric.value_type === "decimal") {
          return numericValue.toFixed(2);
        }
        return Intl.NumberFormat('en', { notation: 'compact', maximumFractionDigits: 1 }).format(numericValue);
      },
    },
  };
  </script>
  
  <style scoped>
  .chart_title {
    font-family: "Poppins" !important;
    font-size: 10pt !important;
    line-height: 40px;
  
    display: block;
    max-width: 200px;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
  .chart_style {
    height: 60vh;
    font-family: "Poppins" !important;
    position: relative;
  }
  .chart_legend_bar {
    align-items: center;
    display: flex;
    gap: 8px;
    left: 5%;
    min-height: 32px;
    min-width: 0;
    position: absolute;
    right: 5%;
    top: 44px;
    z-index: 2;
  }
  .audience_filter_button {
    background: #f4f7f6 !important;
    border: 1px solid #d6dfdc;
    border-radius: 16px;
    color: #263b36;
    font-family: "Poppins" !important;
    font-size: 11px;
    font-weight: 600;
    height: 28px;
    letter-spacing: 0;
    flex: 0 0 180px;
    max-width: 180px;
    text-transform: none;
  }
  .audience_filter_button:hover {
    background: #eaf1ef !important;
    border-color: #aebfba;
  }
  .audience_filter_button::v-deep .v-btn__content {
    min-width: 0;
    width: 100%;
  }
  .audience_filter_button__label {
    flex: 1 1 auto;
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
  .audience_filter_menu {
    border-radius: 6px;
    box-shadow: 0 6px 20px rgba(28, 43, 39, 0.16);
    max-width: 300px;
  }
  .audience_filter_list {
    font-family: "Poppins" !important;
    max-height: 280px;
    overflow-y: auto;
  }
  .audience_filter_list::v-deep .v-list-item__title {
    font-family: "Poppins" !important;
    font-size: 12px;
  }
  .audience_filter_list__item--active {
    background: #edf5f3;
    color: #185e50;
  }
  .series_legend {
    align-items: center;
    display: flex;
    flex: 1 1 auto;
    gap: 5px;
    min-width: 0;
    overflow-x: auto;
    height: 32px;
    padding: 2px 1px;
    scrollbar-color: #c4cecb transparent;
    scrollbar-width: thin;
  }
  .series_legend__item {
    align-items: center;
    background: transparent;
    border: 1px solid transparent;
    border-radius: 14px;
    color: #35423f;
    cursor: pointer;
    display: inline-flex;
    flex: 0 0 auto;
    font-family: "Poppins" !important;
    font-size: 11px;
    gap: 6px;
    height: 26px;
    max-width: 210px;
    padding: 0 8px;
    transition: background-color 140ms ease, border-color 140ms ease, opacity 140ms ease;
  }
  .series_legend__item:hover {
    background: #f5f7f7;
    border-color: #dce3e1;
  }
  .series_legend__item--hidden {
    opacity: 0.38;
  }
  .series_legend__swatch {
    border-radius: 50%;
    flex: 0 0 auto;
    height: 8px;
    width: 8px;
  }
  .series_legend__item--hidden .series_legend__swatch {
    background: #aeb7b4 !important;
  }
  .series_legend__label {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
  @media (max-width: 760px) {
    .chart_legend_bar {
      left: 14px;
      right: 14px;
      top: 46px;
    }
    .audience_filter_button {
      flex-basis: 120px;
      max-width: 120px;
    }
  }
  </style>
  
